import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, ExternalLink, CheckCircle, XCircle } from 'lucide-react';

// Utilitaire de stockage sécurisé
const storage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      console.warn('Stockage local non disponible');
    }
  }
};

function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const hasConsent = storage.getItem('cookieConsent');
    if (!hasConsent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    storage.setItem('cookieConsent', 'all');
    setIsVisible(false);
  };

  const handleEssentialOnly = () => {
    storage.setItem('cookieConsent', 'essential');
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    storage.setItem('cookieConsent', 'none');
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700"
        >
          <div className="container mx-auto p-4 md:p-6">
            <div className="relative flex flex-col gap-6">
              {/* En-tête avec icône */}
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-shrink-0">
                  <Cookie className="w-12 h-12 text-blue-400" />
                </div>
                
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-semibold text-white mb-2 flex items-center justify-center md:justify-start gap-2">
                    Vos préférences en matière de cookies
                  </h3>
                  <p className="text-gray-300 text-sm md:text-base max-w-2xl">
                    Nous utilisons des cookies pour améliorer votre expérience de navigation, 
                    personnaliser le contenu et analyser notre trafic. Vous pouvez personnaliser 
                    vos choix ci-dessous.
                  </p>
                </div>

                {/* Bouton de fermeture */}
                <button 
                  onClick={() => setIsVisible(false)}
                  className="absolute right-0 top-0 p-2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Fermer"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Détails des cookies */}
              <div className="space-y-4">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-2"
                >
                  {showDetails ? "Masquer les détails" : "Afficher les détails"}
                  <motion.span
                    animate={{ rotate: showDetails ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    ▼
                  </motion.span>
                </button>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-3 text-sm text-gray-300"
                    >
                      <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-white">Cookies essentiels</h4>
                          <p>Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                        <XCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-white">Cookies analytiques</h4>
                          <p>Nous aident à comprendre comment vous utilisez notre site.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                        <XCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-white">Cookies marketing</h4>
                          <p>Utilisés pour la publicité ciblée et le suivi sur d'autres sites.</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Boutons d'action */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-end">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleRejectAll}
                  className="px-6 py-2.5 text-sm font-medium text-red-300 hover:text-red-200 bg-slate-800/80 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Tout refuser
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleEssentialOnly}
                  className="px-6 py-2.5 text-sm font-medium text-gray-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cookies essentiels uniquement
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Tout accepter
                </motion.button>
                
                <motion.a
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href="/politique-confidentialite"
                  className="px-6 py-2.5 text-sm font-medium text-gray-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  En savoir plus
                  <ExternalLink size={16} />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CookieConsent;